import { Component, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { Answer } from "src/app/Models/profile.interface";
import { VersePrompt } from "src/app/Models/prompt.interface";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { IntervalObservable } from "rxjs/observable/IntervalObservable";
import { UserManager } from "src/app/Services/UserManager/user-manager";
import { EventEmitter } from "@angular/core";
import { debounce } from "lodash";

@Component({
  selector: "question-verse-prompts",
  templateUrl: "./question-verse-prompts.component.html",
  styleUrls: [
    "./question-verse-prompts.component.css",
    "../../templates/question-template/question-template.component.scss",
    "dragula.min.css",
    "svg.scss",
  ],
})
export class QuestionVersePromptsComponent implements OnInit, OnDestroy {
  @Input() config;
  @Input() question;
  @Input() allCorrect: boolean = false;
  @Output() allCorrectChange = new EventEmitter<boolean>();

  answers = {};
  answersArray = Array<any>();
  answerSubscriptions = {};
  bodyID: string;
  deleteTimer: Observable<number>;
  deleteTimerSubscription: Subscription;
  displayAnswers = {};
  dragOptions: any = {
    revertOnSpill: true,
    copy: false,
  };
  leaving = new Subject();
  selectedAnswer: Answer;
  selectedElement;
  shouldScrollToBody: boolean = false;
  showQuestion: boolean = false;
  versePromptIDs = Array<string>();
  versePrompts = Array<VersePrompt>();
  versePrompts$: Observable<VersePrompt[]>;
  vpShown: boolean = true;
  radioDisplayAnswer;
  blankSubs = [];

  constructor(private um: UserManager) {}
  ngOnInit(): void {
    this.versePrompts = this.question.blanks;
    this.um.getProfileSubject().take(2).subscribe({
      next: console.log,
    });
    this.versePromptIDs = this.question.blanks.map(async (blank, i) => {
      const sub = await this.um.getAnswersSubscription(
        this.question.lessonID,
        this.question.questionID,
        +i
      );

      sub.takeUntil(this.leaving).subscribe({
        next: (answer) => {
          if (answer) {
            // note: answers are removed by remove answer function
            this.answers[answer?.blankID] = answer;
          }
          this.checkAfterUpdates();
        },
      });
    });
  }

  checkAfterUpdates = debounce(() => this.checkIfAllCorrect(), 500);

  setup = async () => {};
  ngOnDestroy(): void {
    this.leaving.next();
  }

  setAllCorrect = (value: boolean) => {
    this.allCorrectChange.emit(value);
  };

  onDrop(event: CdkDragDrop<string[]>, id: string) {
    this.deselectAnswer();
    if (event.previousContainer === event.container) {
      moveItemInArray(
        this.question.choices,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      // get id from last container
      let answer = {} as Answer;
      answer.choice = event.item.data;
      // let rawID = id;
      // let splits = rawID.split("-");
      // let _id = splits[splits.length - 1];
      answer.blankID = id; //this.versePrompts[+_id].$key;
      this.saveAnswer(answer);
    }
  }

  getKeys(object) {
    // get the keys from an object and remove the $value key
    var keys = Object.keys(object);
    const index = keys.indexOf("$value");
    if (index > -1) {
      keys.splice(index, 1);
    }
    return keys;
  }

  selectElement(element) {
    if (this.selectedElement) {
      this.deSelectElement();
    }
    //check the classes the element has

    element.classList.add("selected");
    element.classList.remove("unselected");
    this.selectedElement = element;
  }

  deSelectElement() {
    if (!this.selectedElement) {
      return;
    }
    this.selectedElement.classList.remove("selected");
    this.selectedElement.classList.add("unselected");
    this.selectedElement = null;
  }

  selectChoice(choice: string, event) {
    event.stopPropagation();
    var answer = {} as Answer;
    answer.choice = choice;
    this.selectedAnswer = answer;
    this.deSelectElement();
    this.selectElement(event.target);
  }

  deselectAnswer() {
    this.selectedAnswer = null;
  }

  removeAnswer(answer: Answer) {
    if (this.answers[answer.blankID]) {
      // console.log('called remove')
      this.um
        .removeAnswer(answer, this.question.lessonID, this.question.questionID)
        .then((_) => this.checkIfAllCorrect())
        .catch((e) => console.error(e));
      this.question.choices = this.question.choices.filter(
        (choice) => choice !== answer.choice && choice
      );
      this.question.choices.push(answer.choice);
      this.answers[answer.blankID] = null;
    }
  }

  selectAnswer(answer: Answer, event, force = null) {
    event.stopPropagation();
    if (force) {
      this.removeAnswer(answer);
      return;
    }
    if (!!this.selectedAnswer && !!this.deleteTimer) {
      if (this.answers[answer.blankID]) {
        this.removeAnswer(this.selectedAnswer);
      }
      this.selectedAnswer = null;
      this.deSelectElement();
      this.clearTimer();
      return;
    }
    answer.choice = answer.choice.replace(/\s/g, "");
    this.selectedAnswer = answer;
    this.selectElement(event.target);
    if (!this.deleteTimer) {
      this.deleteTimer = IntervalObservable.create(500);
      this.deleteTimerSubscription = this.deleteTimer
        .takeUntil(this.leaving)
        .subscribe(() => this.clearTimer());
    }
  }

  clearTimer() {
    this.deleteTimerSubscription.unsubscribe();
    this.deleteTimer = null;
  }

  selectBlank(vp: VersePrompt) {
    if (!this.selectedAnswer) {
      return;
    }
    if (
      !!this.selectedAnswer?.blankID &&
      this.selectedAnswer?.blankID !== vp.$key
    ) {
      this.removeAnswer(this.selectedAnswer);
    }

    this.selectedAnswer.blankID = vp.$key;
    this.saveAnswer(this.selectedAnswer)
      .then(() => {
        this.selectedAnswer = null;
        this.deSelectElement();
      })
      .catch((e) => {
        console.error(e);
        this.selectedAnswer = null;
        this.deSelectElement();
      });
  }

  shouldShowChoice(choice: string): boolean {
    var keys = this.getKeys(this.answers);
    if (keys.length <= 0 || !keys) {
      return true;
    }
    for (let key of keys) {
      if (this.answers[key] && choice == this.answers[key].choice) {
        return false;
      }
    }
    return true;
  }

  createBlankID(lessonID: string, questionID: string, blankid: number): string {
    return `${lessonID}-${questionID}-${blankid}`;
  }

  createBodyID(lessonID: string, questionID: string) {
    return `${lessonID}-${questionID}-body`;
  }

  saveAnswer = async (answer: Answer) => {
    if (!answer) {
      return;
    }
    var vp = this.versePrompts[answer.blankID];
    this.shouldScrollToBody = false;
    answer.correct = vp.correctAnswer === answer.choice ? true : false;
    try {
      await this.um.saveAnswer(
        answer,
        this.question.lessonID,
        this.question.questionID
      );
    } catch (e) {
      console.error(e);
    } finally {
      this.checkIfAllCorrect();
    }
  };

  saveRadioAnswer(choice: string) {
    const vp = this.versePrompts[0];
    var answer = {} as Answer;
    // console.log(answer.choice)
    answer.choice = choice;
    answer.blankID = "0";
    answer.correct = vp.correctAnswer === choice ? true : false;
    this.um.saveAnswer(
      answer,
      this.question.lessonID,
      this.question.questionID
    );
    this.shouldScrollToBody = false;
    // this.answers[vp.$key] = answer
    this.radioDisplayAnswer = choice;
    this.checkIfAllCorrect();
  }

  saveTrueOrFalse(choice: string) {
    var answer = {} as Answer;
    answer.choice = choice;
    answer.correct = this.question.correctAnswer === choice ? true : false;
    answer.blankID = "0";
    this.shouldScrollToBody = false;
    this.um.saveAnswer(
      answer,
      this.question.lessonID,
      this.question.questionID
    );
    this.checkIfAllCorrect();
  }

  checkIfAllCorrect() {
    console.log("checking");
    var answerKeys = this.getKeys(this.answers);
    if (
      this.question.questionType === "trueOrFalse" ||
      this.question.questionType === "radio" ||
      this.question.questionType === "truthConfirmation"
    ) {
      this.gradeOthers(answerKeys);
    } else if (this.question.questionType === "wordCloud") {
      this.gradeWordCloud(answerKeys);
    } else {
    }
  }

  gradeOthers(answerKeys: string[]) {
    // should only be one key for true or false questions
    if (answerKeys.length === 1) {
      for (let key of answerKeys) {
        var answer: Answer = this.answers[key];
        if (!answer || !answer?.correct) {
          this.markIncorrect();
          return;
        }
      }
      this.markCorrect();
    } else {
      // no answer, by default incorrect
      // no answers, so false by default
      this.markIncorrect();
    }
  }

  gradeWordCloud(answerKeys: string[]) {
    // console.log(answerKeys.length )
    // console.log( this.versePrompts.length )
    if (
      answerKeys.length === this.versePrompts.length &&
      this.versePrompts.length != 0
    ) {
      for (let key of answerKeys) {
        var answer: Answer = this.answers[key];
        // console.log(`answer before check  = ${answer}`)
        if (!answer || !answer?.correct) {
          this.markIncorrect();
          return;
        }
      }
      this.markCorrect();
    } else {
      this.markIncorrect();
    }
  }

  truthConfirmed(choice: string) {
    this.allCorrect = true; // set the confirmation as true but save the actual answer.  if false, open the "have a question dialog"
    var answer = {} as Answer;
    answer.choice = choice;
    answer.correct = true; // this.question.correctAnswer === choice ? true : false
    answer.blankID = "0";
    this.answers["0"] = answer;
    this.um.saveAnswer(
      answer,
      this.question.lessonID,
      this.question.questionID,
      true
    );
    this.checkIfAllCorrect();
  }

  markCorrect() {
    this.setAllCorrect(true);
  }
  markIncorrect() {
    this.setAllCorrect(false);
  }
  closeModal(e) {}
}
